/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Checkbox, Col, Radio, Row, Select, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import BaseForm from 'components/modules/BaseForm';
import Spin from 'components/modules/Spin';
import { useAppSelector } from 'flex/utils';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useViewTenantNewTermSchoolClass } from '../hooks/useViewTenantNewTermSchoolClass';
import { useSaveTenantNewTermSchoolClass } from '../hooks/useSaveTenantNewTermSchoolClass';
import { getMoment, TENANT_NEW_TERM_SCHOOL_CLASS_SETTING } from 'constants/GlobalConfig';
import { checkFormChange, isEmptyInput } from 'utils/Helper';
import { useViewNewTermSchoolClass } from '../hooks/useViewNewTermSchoolClass';

const moment = getMoment();
const Form = BaseForm;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 12 },
        md: { span: 12 },
        xl: { span: 8 },
        xxl: { span: 6 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 12 },
        md: { span: 12 },
        xl: { span: 16 },
        xxl: { span: 18 },
    },
};

const CURRENT_YEAR = moment().year();
const DAY_DEFAULT = 10;
const MONTH_DEFAULT = 4;

// antd Form.Itemのレイアウト (ラベルなし)
export const formItemLayoutWithoutLabel = {
    wrapperCol: {
        sm: { span: 24, offset: 12 },
        md: { span: 24, offset: 12 },
        xl: { span: 24, offset: 8 },
        xxl: { span: 24, offset: 6 },
    },
};

type Props = {
    tabActiveKey: string;
};

const TenantNewTermSchoolClassCard: React.FC<Props> = ({ tabActiveKey }) => {
    const [form] = Form.useForm();
    const [viewTenantState, viewTenantRequest] = useViewTenantNewTermSchoolClass();
    const [saveState, saveRequest] = useSaveTenantNewTermSchoolClass();
    const [viewState, viewRequest] = useViewNewTermSchoolClass();
    const canAdminUpdateVisibility = Form.useWatch('canAdminUpdateVisibility', form);
    const visibilityTiming = Form.useWatch('visibilityTiming', form);
    const isControllableUser = useAppSelector((state) => state.isControllableUser.payload);
    const [formInitValue, setFormInitValue] = useState({});
    const [formChangeValue, setFormChangeValue] = useState({});
    const [isDisabledSubmitBtn, setIsDisabledSubmitBtn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabledFeature, setIsDisabledFeature] = useState(false);
    const [dayOptionMax, setDayOptionMax] = useState(30);

    useEffect(() => {
        if (tabActiveKey !== 'settings') return;
        viewTenantRequest(isControllableUser);
        if (!isControllableUser) {
            viewRequest();
        }
        setIsLoading(true);
    }, [tabActiveKey]);

    useEffect(() => {
        if (viewTenantState?.data) {
            if (isControllableUser) {
                form.setFieldsValue({
                    visibilityTiming: viewTenantState.data.visibilityTiming,
                    visibilityScheduledMonth: viewTenantState.data.visibilityScheduledMonth,
                    visibilityScheduledDay: viewTenantState.data.visibilityScheduledDay,
                    canAdminUpdateVisibility: viewTenantState.data.canAdminUpdateVisibility,
                    overwriteAdminVisibilitySchedules: viewTenantState.data.overwriteAdminVisibilitySchedules,
                });
                setDayOptionMax(getMaxDayInMonth(viewTenantState.data.visibilityScheduledMonth ?? MONTH_DEFAULT))
                setFormInitValue(form.getFieldsValue(true));
            } else {
                setIsDisabledFeature(!viewTenantState.data.canAdminUpdateVisibility);
            }
        }
        setIsLoading(viewTenantState.isLoading);
        setIsDisabledSubmitBtn(true);
    }, [viewTenantState]);

    useEffect(() => {
        if (viewState?.data && !isControllableUser) {
            form.setFieldsValue({
                visibilityTiming: viewState.data.visibilityTiming,
                visibilityScheduledMonth: viewState.data.visibilityScheduledMonth,
                visibilityScheduledDay: viewState.data.visibilityScheduledDay,
            });
            setDayOptionMax(getMaxDayInMonth(viewState.data.visibilityScheduledMonth ?? MONTH_DEFAULT))
            setFormInitValue(form.getFieldsValue(true));
        }
        setIsLoading(viewState.isLoading);
        setIsDisabledSubmitBtn(true);
    }, [viewState]);

    useEffect(() => {
        const isFormChange = checkFormChange(formInitValue, formChangeValue);
        setIsDisabledSubmitBtn(!isFormChange);
    }, [formChangeValue]);

    useEffect(() => {
        if (saveState.isSuccessful && !saveState.isLoading) {
            if (isControllableUser) {
                form.setFieldsValue({
                    visibilityTiming: saveState?.tenantNewTermSchoolClass?.visibilityTiming,
                    visibilityScheduledMonth: saveState?.tenantNewTermSchoolClass?.visibilityScheduledMonth,
                    visibilityScheduledDay: saveState?.tenantNewTermSchoolClass?.visibilityScheduledDay,
                    canAdminUpdateVisibility: saveState?.tenantNewTermSchoolClass?.canAdminUpdateVisibility,
                    overwriteAdminVisibilitySchedules: saveState?.tenantNewTermSchoolClass?.overwriteAdminVisibilitySchedules,
                })
            } else {
                form.setFieldsValue({
                    visibilityTiming: saveState?.newTermSchoolClass?.visibilityTiming,
                    visibilityScheduledMonth: saveState?.newTermSchoolClass?.visibilityScheduledMonth,
                    visibilityScheduledDay: saveState?.newTermSchoolClass?.visibilityScheduledDay,
                })
            }
            setFormInitValue(form.getFieldsValue(true));
        }
        setIsDisabledSubmitBtn(true);
        setIsLoading(saveState.isLoading);
    }, [saveState]);

    const handelChangeVisibilityTiming = (value: number) => {
        const viewData = isControllableUser ? viewTenantState : viewState;
        const day = !isEmptyInput(viewData.data?.visibilityScheduledDay)
            ? viewData.data?.visibilityScheduledDay
            : value === TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_SCHEDULED
                ? DAY_DEFAULT
                : null;

        const month = !isEmptyInput(viewData.data?.visibilityScheduledMonth)
            ? viewData.data?.visibilityScheduledMonth
            : value === TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_SCHEDULED
                ? MONTH_DEFAULT
                : null;

        form.setFieldsValue({
            visibilityScheduledDay: day,
            visibilityScheduledMonth: month,
        });
        handleValuesChange(form.getFieldsValue(true));
    };

    const handleFinish = (values: any) => {
        saveRequest(values, isControllableUser);
        setIsLoading(true);
    };

    const handleSubmit = () => {
        form.submit();
    };

    const handleValuesChange = (valuesChange: any) => {
        setFormChangeValue(valuesChange);
    };

    const handleChangeUpdateVisibility = (isCanUpdate: boolean) => {
        if (!isCanUpdate) {
            form.setFieldsValue({
                overwriteAdminVisibilitySchedules: !isCanUpdate,
            });
        } else {
            form.setFieldsValue({
                overwriteAdminVisibilitySchedules: viewTenantState.data?.overwriteAdminVisibilitySchedules,
            });
        }
        handleValuesChange(form.getFieldsValue());
    };

    /***
     * 指定された月の中で一番大きい日を返す。
     * @param {number} month
     * @return {number}
     */
    const getMaxDayInMonth = (month: number) => {
        switch (month) {
            case 2:
                return 28;
            case 4:
            case 6:
            case 9:
            case 11:
                return 30;
            default:
                return 31;
        }
    }

    const handleChangeMonth = (month: number) => {
        const maxDayInMonth = getMaxDayInMonth(month);
        setDayOptionMax(maxDayInMonth);

        const currentDay = form.getFieldValue('visibilityScheduledDay');
        switch (month) {
            case 4:
            case 6:
                if (currentDay > maxDayInMonth) {
                    form.setFieldsValue({ visibilityScheduledDay: maxDayInMonth });
                }
                break;
            default:
                break;
        }
    };

    return (
        <div className='card' style={{ marginBottom: '32px', padding: '32px' }}>
            {isLoading ? (
                <div className='flex-center-container' style={{ height: '200px' }}>
                    <Spin />
                </div>
            ) : (
                <div>
                    <p className='gutter-bottom text-lg' style={{ fontWeight: 'bold' }}>
                        クラスの公開日
                    </p>
                    <span className='gutter-bottom' style={{ fontWeight: 'bold' }}>
                        {CURRENT_YEAR}年度のクラスを、ユーザーホーム画面の「設定」メニューの「あなたのプロフィール」に表示する日を設定します。
                    </span>
                    <br />
                    <span className='gutter-bottom' style={{ fontWeight: 'bold' }}>
                        設定したクラスの表示日になるまで、「あなたのプロフィール」に{CURRENT_YEAR}年度のクラスは表示されなくなります。
                    </span>
                    <br />
                    <p className='gutter-bottom'>※注意:「変更」ボタンを押すまで、変更は完了しません。</p>

                    <Form
                        {...formItemLayout}
                        form={form}
                        name='control-hooks-edit-user'
                        onFinish={handleFinish}
                        onValuesChange={(_, allValues) => handleValuesChange(allValues)}>
                        <Form.Item label={`${CURRENT_YEAR}年度のクラス表示日`} name='visibilityTiming' required>
                            <Radio.Group
                                onChange={(event) => handelChangeVisibilityTiming(event.target.value)}
                                disabled={isDisabledFeature}>
                                <Radio value={TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_IMMEDIATE}>
                                    即時
                                </Radio>
                                <Radio value={TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_SCHEDULED}>
                                    指定日
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            {...formItemLayoutWithoutLabel}
                            hidden={
                                visibilityTiming === TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_IMMEDIATE
                            }>
                            <Row gutter={[8, 0]} style={{ alignItems: 'center' }}>
                                <Typography.Text style={{ marginBottom: '24px', marginRight: '10px' }}>
                                    {CURRENT_YEAR}年
                                </Typography.Text>
                                <Col>
                                    <Form.Item name='visibilityScheduledMonth'>
                                        <Select onChange={handleChangeMonth} disabled={isDisabledFeature}>
                                            {Array.from({ length: 4 }, (_, index) => index + 4).map((i) => (
                                                <Select.Option key={i} value={i}>{`${i}月`}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col>
                                    <Form.Item
                                        name='visibilityScheduledDay'
                                        rules={[
                                            {
                                                required:
                                                    visibilityTiming ===
                                                    TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_SCHEDULED,
                                            },
                                        ]}>
                                        <Select disabled={isDisabledFeature}>
                                            {Array.from({ length: dayOptionMax }, (_, index) => index + 1).map((i) => (
                                                <Select.Option key={i} value={i}>{`${i}日`}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form.Item>
                        {isControllableUser && (
                            <Form.Item
                                label={
                                    <span>
                                        学校管理画面での公開日変更
                                        <Tooltip title='配信先の学校で、学校管理者が表示日を任意に変更できるかどうかを設定できます。'>
                                            <QuestionCircleOutlined className='icon-l' />
                                        </Tooltip>
                                    </span>
                                }>
                                <Form.Item
                                    name='canAdminUpdateVisibility'
                                    style={{
                                        display: 'inline-block',
                                        marginBottom: 0,
                                        marginRight: '1rem',
                                    }}>
                                    <Radio.Group onChange={(event) => handleChangeUpdateVisibility(event.target.value)}>
                                        <Radio value={false}>許可しない</Radio>
                                        <Radio value={true}>許可する</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Row align='middle' style={{ display: 'inline-flex' }}>
                                    <Form.Item
                                        name='overwriteAdminVisibilitySchedules'
                                        style={{
                                            display: 'inline-block',
                                            marginBottom: 0,
                                        }}
                                        valuePropName='checked'
                                        >
                                        <Checkbox disabled={!canAdminUpdateVisibility}>
                                            学校で個別に設定されている公開日を上書きする
                                        </Checkbox>
                                    </Form.Item>
                                    <Tooltip title='「許可しない」から「許可する」に変更した際は、この選択に関わらず学校管理画面での表示日も変更されます。'>
                                        <QuestionCircleOutlined className='icon-l' />
                                    </Tooltip>
                                </Row>
                            </Form.Item>
                        )}
                    </Form>
                </div>
            )}

            <div className='flex-right-container'>
                <Button
                    className='mb-4'
                    type='primary'
                    onClick={handleSubmit}
                    disabled={isDisabledSubmitBtn || isDisabledFeature}
                    loading={isLoading}>
                    変更
                </Button>
            </div>
        </div>
    );
};

export default TenantNewTermSchoolClassCard;
